import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Building2, Home as HomeIcon, Layers, Cpu, Eye, Star } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

function MiniGallerySection() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  useEffect(() => {
    publicApi
      .get("/gallery/public", { params: { limit: 3, pinned_only: true } })
      .then((r) => {
        const apiItems = r.data.items || [];
        if (apiItems.length > 0) {
          setItems(apiItems);
        } else {
          // Fallback: static seed assets shipped with the build (works on any deployment
          // even before backend has data)
          setItems([
            {
              image_id: "dc714fb9",
              sim_type: "INTERIOR",
              style: "ART_DECO",
              after_url_static: "/gallery_seed/dc714fb9_after.jpg",
            },
            {
              image_id: "6efc3519",
              sim_type: "INTERIOR",
              style: "MODERN",
              after_url_static: "/gallery_seed/6efc3519_after.jpg",
            },
            {
              image_id: "ae3754f2",
              sim_type: "INTERIOR",
              style: "CLASSIC",
              after_url_static: "/gallery_seed/ae3754f2_after.jpg",
            },
          ]);
        }
      })
      .catch(() => {
        setItems([
          {
            image_id: "dc714fb9",
            sim_type: "INTERIOR",
            style: "ART_DECO",
            after_url_static: "/gallery_seed/dc714fb9_after.jpg",
          },
          {
            image_id: "6efc3519",
            sim_type: "INTERIOR",
            style: "MODERN",
            after_url_static: "/gallery_seed/6efc3519_after.jpg",
          },
          {
            image_id: "ae3754f2",
            sim_type: "INTERIOR",
            style: "CLASSIC",
            after_url_static: "/gallery_seed/ae3754f2_after.jpg",
          },
        ]);
      });
  }, []);

  if (!items.length) return null;

  const TYPE_LABELS = { INTERIOR: "Interior", EXTERIOR: "Exterior" };
  const STYLE_LABELS = {
    ART_DECO: "Art Deco",
    CLASSIC: "Classic",
    MODERN: "Modern",
    NEOCLASSIC: "Neoclassic",
  };

  return (
    <section
      className="relative py-28 px-6 lg:px-10 border-t border-white/5"
      data-testid="landing-mini-gallery"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-4">
              {t("landing.mini_gallery_eyebrow")}
            </div>
            <h2 className="font-outfit font-light text-white text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              {t("landing.mini_gallery_title_prefix")}{" "}
              <span className="text-orange-400">{t("landing.mini_gallery_title_highlight")}</span>.
            </h2>
          </div>
          <Link to="/galerie" data-testid="landing-gallery-see-all">
            <Button
              variant="outline"
              className="border-white/15 text-white bg-white/5 hover:bg-white/10 hover:text-white"
            >
              {t("landing.mini_gallery_see_all")}
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.map((it) => (
            <Link
              key={it.image_id}
              to={`/galerie#${it.image_id}`}
              className="group relative block rounded-xl overflow-hidden border border-white/10 bg-zinc-900/40 hover:border-orange-500/60 transition"
              data-testid={`landing-gallery-${it.image_id}`}
            >
              <div className="aspect-[4/3] w-full bg-black overflow-hidden">
                <img
                  src={
                    it.after_url_static
                      ? it.after_url_static
                      : `${BACKEND_URL}${it.after_url}`
                  }
                  alt="render"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
                <div className="text-[10px] uppercase tracking-[0.3em] text-orange-400 font-bold mb-1">
                  {TYPE_LABELS[it.sim_type]} · {STYLE_LABELS[it.style]}
                </div>
                <div className="text-xs text-white/90 flex items-center gap-1">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star
                      key={i}
                      size={10}
                      className="fill-orange-400 text-orange-400"
                    />
                  ))}
                  <span className="ml-1 text-white/60">{t("landing.mini_gallery_recommended")}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}


const HERO_IMG =
  "https://images.unsplash.com/photo-1638885930125-85350348d266?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc3Njk5ODAxfDA&ixlib=rb-4.1.0&q=85";
const INTERIOR_IMG =
  "https://images.unsplash.com/photo-1638454795595-0a0abf68614d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc3Njk5ODAxfDA&ixlib=rb-4.1.0&q=85";
const EXTERIOR_IMG =
  "https://images.unsplash.com/photo-1757439402296-000be181e38b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBleHRlcmlvciUyMG5pZ2h0fGVufDB8fHx8MTc3NzY5OTgwMXww&ixlib=rb-4.1.0&q=85";

const STYLE_IMGS = {
  ART_DECO:
    "https://images.unsplash.com/photo-1758541214332-3cb54533c469?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxjbGFzc2ljJTIwYXJjaGl0ZWN0dXJhbCUyMHdhbGwlMjBtb2xkaW5nJTIwZGV0YWlsfGVufDB8fHx8MTc3NzY5OTgwMXww&ixlib=rb-4.1.0&q=85",
  CLASSIC:
    "https://images.unsplash.com/photo-1762795966465-283c02fb3e92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxjbGFzc2ljJTIwYXJjaGl0ZWN0dXJhbCUyMHdhbGwlMjBtb2xkaW5nJTIwZGV0YWlsfGVufDB8fHx8MTc3NzY5OTgwMXww&ixlib=rb-4.1.0&q=85",
  MODERN:
    "https://images.unsplash.com/photo-1621293954908-907159247fc8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc3Njk5ODAxfDA&ixlib=rb-4.1.0&q=85",
  NEOCLASSIC:
    "https://images.unsplash.com/photo-1761471567052-011445647b89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxjbGFzc2ljJTIwYXJjaGl0ZWN0dXJhbCUyMHdhbGwlMjBtb2xkaW5nJTIwZGV0YWlsfGVufDB8fHx8MTc3NzY5OTgwMXww&ixlib=rb-4.1.0&q=85",
};

export default function LandingPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  // For logged-in users, the "create account" CTAs should send them straight
  // to their dashboard (or admin panel) instead of /register — which would be
  // intercepted by PublicOnly and redirected anyway, but feels inconsistent.
  const ctaTo = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/register";
  const ctaPrimaryLabel = user ? t("nav.dashboard") : t("landing.cta_start_free");
  const ctaBottomLabel = user ? t("nav.dashboard") : t("nav.register");
  return (
    <div className="min-h-screen bg-background grain" data-testid="landing-page">
      <Navbar />

      {/* Beta status strip — anchored in the normal flow just below the
          translucent navbar so it no longer overlaps the hero eyebrow on
          short laptop viewports. The hero adjusts its own min-height to
          {/* iter96 · BetaBanner removed per user feedback (production ready) */}

      {/* HERO — viewport-tall on desktop, falls back to a generous 700px
          floor on shorter laptop screens so the banner + eyebrow + title +
          subhead never collide. */}
      <section className="relative min-h-[700px] lg:min-h-[calc(100vh-7rem)] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt={t("landing.hero_image_alt")}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/55 to-black/85" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(238,116,64,0.18),transparent_55%)]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-center pt-24 sm:pt-16">
          <div className="max-w-3xl">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 mb-8 border border-orange-500/40 rounded-full bg-orange-500/10 text-orange-300 text-xs uppercase tracking-[0.25em]"
              data-testid="hero-eyebrow"
            >
              <Sparkles size={12} strokeWidth={1.5} />
              <span>{t("landing.hero_tag")}</span>
            </div>

            <h1 className="font-outfit font-black text-white text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter text-balance">
              {t("landing.hero_title_1")} {t("landing.hero_title_2")}
              <br />
              <span className="text-orange-500 italic font-cormorant font-normal">
                {t("landing.hero_title_serif")}
              </span>
            </h1>

            <p className="mt-8 max-w-xl text-zinc-300 text-lg md:text-xl leading-relaxed font-manrope">
              {t("landing.hero_subtitle")}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to={ctaTo} data-testid="hero-cta-register">
                <Button
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white btn-orange-glow h-14 px-8 text-base font-medium"
                >
                  {ctaPrimaryLabel}
                  <ArrowRight size={18} strokeWidth={2} className="ml-2" />
                </Button>
              </Link>
              {!user && (
                <Link to="/login" data-testid="hero-cta-login">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 text-base bg-white/5 text-white border-white/15 hover:bg-white/10 hover:text-white"
                  >
                    {t("landing.cta_have_account")}
                  </Button>
                </Link>
              )}
              <Link to="/galerie" data-testid="hero-cta-gallery">
                <Button
                  size="lg"
                  variant="ghost"
                  className="h-14 px-6 text-base text-orange-400 hover:bg-orange-500/10 hover:text-orange-300"
                >
                  {t("landing.cta_see_gallery")}
                  <ArrowRight size={18} strokeWidth={2} className="ml-2" />
                </Button>
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-8 max-w-lg">
              <div>
                <div className="font-outfit text-3xl font-black text-white">559+</div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">
                  {t("landing.stat_products")}
                </div>
              </div>
              <div>
                <div className="font-outfit text-3xl font-black text-white">4</div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">
                  {t("landing.stat_styles")}
                </div>
              </div>
              <div>
                <div className="font-outfit text-3xl font-black text-orange-500">5</div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">
                  {t("landing.stat_renders_per_sim")}
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-10 right-10 hidden md:block text-right">
            <div className="font-cormorant italic text-zinc-400 text-2xl leading-tight whitespace-pre-line">
              {t("landing.footer_signature")}
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mt-3">
              {t("landing.footer_since")}
            </div>
          </div>
        </div>
      </section>

      {/* iter96 · Removed "INTRO / ABOUT OneFDP" section per user feedback —
          the disclaimer content moved to SimulationDetailPage (visible on
          generated images page, where it's contextually relevant). */}

      {/* DUAL DASHBOARDS */}
      <section className="relative py-28 px-6 lg:px-10" data-testid="dual-dashboards-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
            <div className="md:col-span-6">
              <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-4">
                {t("landing.section_02_eyebrow")}
              </div>
              <h2 className="font-outfit font-light text-white text-4xl md:text-6xl tracking-tighter leading-[0.95]">
                {t("landing.section_02_title_1")}{" "}
                <span className="font-cormorant italic">{t("landing.section_02_title_or")}</span>{" "}
                {t("landing.section_02_title_2")}
                <br />
                <span className="font-black">{t("landing.section_02_title_3")}</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <p className="text-zinc-400 text-lg font-manrope leading-relaxed">
                {t("landing.section_02_body")}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="group relative h-[480px] overflow-hidden rounded-lg border border-white/10 hover:border-orange-500/60 transition-all duration-500">
              <img
                src={INTERIOR_IMG}
                alt="Interior"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-between p-10">
                <div className="flex items-center gap-3">
                  <HomeIcon size={20} strokeWidth={1.5} className="text-orange-500" />
                  <span className="text-xs uppercase tracking-[0.3em] text-orange-500">
                    {t("landing.card_dashboard")}
                  </span>
                </div>
                <div>
                  <div className="font-cormorant text-7xl text-white/20 leading-none">01</div>
                  <h3 className="font-outfit font-black text-white text-5xl tracking-tighter mt-2">
                    {t("landing.card_interior_title")}
                  </h3>
                  <p className="text-zinc-300 mt-4 max-w-md">
                    {t("landing.card_interior_body")}
                  </p>
                </div>
              </div>
            </div>

            <div className="group relative h-[480px] overflow-hidden rounded-lg border border-white/10 hover:border-orange-500/60 transition-all duration-500">
              <img
                src={EXTERIOR_IMG}
                alt="Exterior"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-between p-10">
                <div className="flex items-center gap-3">
                  <Building2 size={20} strokeWidth={1.5} className="text-orange-500" />
                  <span className="text-xs uppercase tracking-[0.3em] text-orange-500">
                    {t("landing.card_dashboard")}
                  </span>
                </div>
                <div>
                  <div className="font-cormorant text-7xl text-white/20 leading-none">02</div>
                  <h3 className="font-outfit font-black text-white text-5xl tracking-tighter mt-2">
                    {t("landing.card_exterior_title")}
                  </h3>
                  <p className="text-zinc-300 mt-4 max-w-md">
                    {t("landing.card_exterior_body")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MINI GALLERY — social proof */}
      <MiniGallerySection />

      {/* HOW IT WORKS */}
      <section className="relative py-28 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-4">
            {t("landing.section_03_eyebrow")}
          </div>
          <h2 className="font-outfit font-light text-white text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-16">
            {t("landing.section_03_title")}
          </h2>

          <div className="grid md:grid-cols-3 gap-px bg-white/5">
            {[
              {
                num: "01",
                icon: <Eye size={28} strokeWidth={1.5} />,
                titleKey: "landing.step_1_title",
                textKey: "landing.step_1_text",
              },
              {
                num: "02",
                icon: <Layers size={28} strokeWidth={1.5} />,
                titleKey: "landing.step_2_title",
                textKey: "landing.step_2_text",
              },
              {
                num: "03",
                icon: <Cpu size={28} strokeWidth={1.5} />,
                titleKey: "landing.step_3_title",
                textKey: "landing.step_3_text",
              },
            ].map((s) => (
              <div
                key={s.num}
                className="bg-background p-10 hover:bg-zinc-900/40 transition-colors group"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="font-cormorant text-5xl text-zinc-700 group-hover:text-orange-500/70 transition-colors">
                    {s.num}
                  </div>
                  <div className="text-orange-500">{s.icon}</div>
                </div>
                <h3 className="font-outfit font-bold text-white text-2xl tracking-tight mb-3">
                  {t(s.titleKey)}
                </h3>
                <p className="text-zinc-400 leading-relaxed">{t(s.textKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STYLES */}
      <section className="relative py-28 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-6">
              <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-4">
                {t("landing.section_04_eyebrow")}
              </div>
              <h2 className="font-outfit font-light text-white text-4xl md:text-6xl tracking-tighter leading-[0.95]">
                {t("landing.section_04_title_1")}{" "}
                <span className="font-cormorant italic">{t("landing.section_04_title_serif")}</span>
                {t("landing.section_04_title_2") || "."}
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <p className="text-zinc-400 text-lg leading-relaxed">
                {t("landing.section_04_subtitle")}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { id: "ART_DECO", label: "Art Deco" },
              { id: "CLASSIC", label: "Classic" },
              { id: "MODERN", label: "Modern" },
              { id: "NEOCLASSIC", label: "Neoclassic" },
            ].map((s) => (
              <div
                key={s.id}
                className="relative h-72 overflow-hidden rounded-lg border border-white/10 group"
                data-testid={`style-card-${s.id.toLowerCase()}`}
              >
                <img
                  src={STYLE_IMGS[s.id]}
                  alt={s.label}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <div className="font-outfit font-black text-white text-2xl uppercase tracking-tight">
                    {s.label}
                  </div>
                  <div className="h-px w-12 bg-orange-500 mt-2 group-hover:w-full transition-all duration-500" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-outfit font-black text-white text-5xl md:text-7xl tracking-tighter leading-[0.95]">
            {t("landing.cta_title_1")} <br />
            <span className="text-orange-500">{t("landing.cta_title_2")}</span>
          </h2>
          <p className="text-zinc-400 mt-6 text-lg max-w-xl mx-auto">
            {user
              ? t("landing.cta_subtitle_logged_in")
              : t("landing.cta_subtitle_anonymous")}
          </p>
          <Link to={ctaTo} data-testid="bottom-cta-register" className="inline-block mt-10">
            <Button
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white btn-orange-glow h-14 px-10 text-base"
            >
              {ctaBottomLabel}
              <ArrowRight size={18} strokeWidth={2} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-10 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-600">
            {t("landing.footer_copyright", { year: new Date().getFullYear() })}
          </div>
          <div className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-zinc-600">
            <a
              href="/politica-confidentialitate"
              className="hover:text-orange-400 transition"
              data-testid="landing-footer-privacy-link"
            >
              {t("landing.footer_privacy_link")}
            </a>
            <span className="text-zinc-700">·</span>
            <a
              href="/termeni-si-conditii"
              className="hover:text-orange-400 transition"
              data-testid="landing-footer-terms-link"
            >
              Termeni și condiții
            </a>
            <span className="text-zinc-700">·</span>
            <span>{t("landing.footer_operator")}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
